import { LoaderCircle } from "lucide-react";
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useState,
} from "react";

import { ApiError, apiRequest } from "./lib/apiClient";
import { Brand } from "./components/Brand";
import { LoginPanel } from "./components/LoginPanel";
import { LegalPage } from "./pages/LegalPage";
import { PairingPage } from "./pages/PairingPage";
import type { PublicConfig, VoiceSession } from "./types";

const AdminDashboard = lazy(() =>
  import("./pages/AdminDashboard").then((module) => ({
    default: module.AdminDashboard,
  })),
);
const CustomerDashboard = lazy(() =>
  import("./pages/CustomerDashboard").then((module) => ({
    default: module.CustomerDashboard,
  })),
);
const VoiceRoomPage = lazy(() =>
  import("./pages/VoiceRoomPage").then((module) => ({
    default: module.VoiceRoomPage,
  })),
);

type Administrator = { id: string; email: string; role: string };
type CustomerUser = {
  id: string;
  customerId: string;
  email: string;
  role: string;
};

export function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";

  if (path === "/admin") return <AdminRoute />;
  if (path === "/customer") return <CustomerRoute />;
  if (path === "/legal/terms") return <LegalPage page="terms" />;
  if (path === "/legal/privacy") return <LegalPage page="privacy" />;
  if (path === "/legal/moderation") return <LegalPage page="moderation" />;
  return <VoiceRoute />;
}

function VoiceRoute() {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [session, setSession] = useState<VoiceSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [serviceError, setServiceError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const publicConfig = await apiRequest<PublicConfig>("/api/v1/config/public");
      setConfig(publicConfig);
      try {
        setSession(await apiRequest<VoiceSession>("/api/v1/voice/session"));
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 401) throw error;
        setSession(null);
      }
      setServiceError("");
    } catch (error) {
      setServiceError(
        error instanceof Error ? error.message : "Le service Vox est indisponible.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <FullPageLoading label="Connexion au service Vox" />;
  if (serviceError || !config) {
    return (
      <ServiceUnavailable
        message={serviceError || "La configuration du service est indisponible."}
        onRetry={() => void load()}
      />
    );
  }
  if (!config.voiceAvailable) {
    return (
      <ServiceUnavailable
        title="Integration publique verrouillee"
        message="Vox est disponible uniquement pour le developpement, le staging et les univers prives autorises."
        onRetry={() => void load()}
      />
    );
  }
  if (!session) return <PairingPage onPaired={() => void load()} />;
  return (
    <Suspense fallback={<FullPageLoading label="Ouverture de la salle" />}>
      <VoiceRoomPage
        session={session}
        config={config}
        onLogout={() => setSession(null)}
      />
    </Suspense>
  );
}

function AdminRoute() {
  const [administrator, setAdministrator] = useState<Administrator | null>(null);
  const [checking, setChecking] = useState(true);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      const response = await apiRequest<{ administrator: Administrator }>(
        "/api/v1/admin/me",
      );
      setAdministrator(response.administrator);
    } catch {
      setAdministrator(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  if (checking) return <FullPageLoading label="Verification de la session" />;
  if (!administrator) {
    return <LoginPanel kind="admin" onAuthenticated={() => void check()} />;
  }
  return (
    <Suspense fallback={<FullPageLoading label="Ouverture de la console" />}>
      <AdminDashboard
        administrator={administrator}
        onLogout={() => setAdministrator(null)}
      />
    </Suspense>
  );
}

function CustomerRoute() {
  const [user, setUser] = useState<CustomerUser | null>(null);
  const [checking, setChecking] = useState(true);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      const response = await apiRequest<{ customerUser: CustomerUser }>(
        "/api/v1/customer/me",
      );
      setUser(response.customerUser);
    } catch {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  if (checking) return <FullPageLoading label="Verification de la session" />;
  if (!user) {
    return <LoginPanel kind="customer" onAuthenticated={() => void check()} />;
  }
  return (
    <Suspense fallback={<FullPageLoading label="Ouverture de votre espace" />}>
      <CustomerDashboard user={user} onLogout={() => setUser(null)} />
    </Suspense>
  );
}

function FullPageLoading({ label }: { label: string }) {
  return (
    <div className="full-page-state">
      <Brand />
      <LoaderCircle className="spin" size={26} />
      <p>{label}</p>
    </div>
  );
}

function ServiceUnavailable({
  title = "Service indisponible",
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="full-page-state error-state">
      <Brand />
      <h1>{title}</h1>
      <p>{message}</p>
      <button className="secondary-button" onClick={onRetry}>Reessayer</button>
    </div>
  );
}
